/**
 * URL contract for saved-view display options. Params are the wire diff over the saved
 * baseline: absent inherits the baseline, and setting a param equal to the baseline
 * deletes it. The server validates the diff and may fall back on an unsupported value.
 * One writer covers every managed axis; sort will extend the shape.
 */

import {sameVisibleFields} from '../contexts/views/view-configuration'

export const LAYOUT_PARAM = 'layout'
export const GROUP_BY_PARAM = 'group_by'
export const SWIMLANE_BY_PARAM = 'swimlane_by'
export const VISIBLE_FIELDS_PARAM = 'visible_fields'
const PAGE_PARAM = 'page'

/** Every param a saved view's configuration occupies in the URL. */
export const SAVED_VIEW_URL_PARAMS = [LAYOUT_PARAM, GROUP_BY_PARAM, SWIMLANE_BY_PARAM, VISIBLE_FIELDS_PARAM] as const

export const LAYOUTS = ['list', 'board'] as const
export type Layout = (typeof LAYOUTS)[number]

export interface SavedViewUrlParams {
  layout: Layout
  groupBy: string | null
  /** Omitted by hosts that do not manage swimlane URL overrides. */
  swimlaneBy?: string | null
  /** Omitted by hosts that do not manage visible fields in the URL. */
  visibleFields?: readonly string[]
}

export interface SavedViewUrlOverrides {
  layout?: Layout
  groupBy?: string | null
  swimlaneBy?: string | null
}

function isLayout(value: string | null): value is Layout {
  return value !== null && (LAYOUTS as readonly string[]).includes(value)
}

export function readSavedViewUrlOverrides(search: URLSearchParams): SavedViewUrlOverrides {
  const rawLayout = search.get(LAYOUT_PARAM)
  const groupBy = search.get(GROUP_BY_PARAM)?.trim() || null
  const swimlaneBy = search.get(SWIMLANE_BY_PARAM)?.trim() || null
  return {
    ...(isLayout(rawLayout) ? {layout: rawLayout} : {}),
    ...(search.has(GROUP_BY_PARAM) ? {groupBy} : {}),
    ...(search.has(SWIMLANE_BY_PARAM) ? {swimlaneBy} : {}),
  }
}

/**
 * Returns the visible-fields override, or `undefined` to inherit the baseline. A
 * present-but-empty param is an explicit empty set (every field hidden). When `knownFields`
 * is given, identifiers outside it are ignored, so a stale or hand-edited link cannot mark the
 * view dirty with fields the board does not offer.
 *
 * Visible fields are client-side: they stay out of the route's query key (see
 * `planningViewSearchParams`), so toggling one never refetches. Only the board shows card
 * fields, so List ignores the param.
 */
export function readVisibleFieldsOverride(
  search: URLSearchParams,
  layout: Layout,
  knownFields?: ReadonlySet<string>,
): readonly string[] | undefined {
  if (layout !== 'board' || !search.has(VISIBLE_FIELDS_PARAM)) return undefined
  const fields = (search.get(VISIBLE_FIELDS_PARAM) ?? '')
    .split(',')
    .map(field => field.trim())
    .filter(field => field && (!knownFields || knownFields.has(field)))
  return [...new Set(fields)]
}

/**
 * Returns updated params after applying a partial change to the resolved shape. Each key
 * in `changes` is treated as the new effective value for that setting; keys you omit are
 * left as-is. An empty parameter explicitly clears a saved axis; an absent parameter
 * inherits the baseline. Swimlane and visible fields are written only when the baseline
 * includes them.
 *
 * Cross-setting rules baked in: layout=list drops every board-only param, and pagination
 * resets when the server's results would change.
 */
export function writeSavedViewOverrides(
  search: URLSearchParams,
  changes: Partial<SavedViewUrlParams>,
  baseline: SavedViewUrlParams,
  current: SavedViewUrlParams,
): URLSearchParams {
  const next = new URLSearchParams(search)
  // A visible-fields-only change leaves the axis params alone. Rewriting them from `current`
  // could add a server-defaulted axis to the URL, changing the query key and refetching.
  if (changes.layout !== undefined || changes.groupBy !== undefined || changes.swimlaneBy !== undefined) {
    writeAxes(next, changes, baseline, current)
  }
  if (baseline.visibleFields !== undefined) {
    writeVisibleFields(next, changes.layout ?? current.layout, changes.visibleFields, baseline.visibleFields)
  }
  return next
}

// Writes layout, grouping, and swimlane, resetting pagination when the server's results change.
function writeAxes(
  params: URLSearchParams,
  changes: Partial<SavedViewUrlParams>,
  baseline: SavedViewUrlParams,
  current: SavedViewUrlParams,
): void {
  const layout = changes.layout ?? current.layout
  const groupBy = nextBoardAxis(changes.groupBy, current.layout, current.groupBy, baseline.groupBy)
  const swimlaneBy = nextBoardAxis(changes.swimlaneBy, current.layout, current.swimlaneBy, baseline.swimlaneBy) ?? null
  const managesSwimlanes = baseline.swimlaneBy !== undefined

  writeOrClear(params, LAYOUT_PARAM, layout, baseline.layout)
  writeBoardAxis(params, GROUP_BY_PARAM, layout, groupBy, baseline.groupBy)
  if (managesSwimlanes) writeBoardAxis(params, SWIMLANE_BY_PARAM, layout, swimlaneBy, baseline.swimlaneBy ?? null)

  const resultsChanged =
    layout !== current.layout ||
    onBoard(layout, groupBy) !== onBoard(current.layout, current.groupBy) ||
    (managesSwimlanes && onBoard(layout, swimlaneBy) !== onBoard(current.layout, current.swimlaneBy))
  if (resultsChanged) params.delete(PAGE_PARAM)
}

// A board axis the change leaves alone keeps its current value, or the baseline when arriving from List.
function nextBoardAxis<T>(change: T | undefined, currentLayout: Layout, current: T, baseline: T): T {
  if (change !== undefined) return change
  return currentLayout === 'board' ? current : baseline
}

// The value an axis actually has on the given layout: board axes do not apply to List.
function onBoard(layout: Layout, value: string | null | undefined): string | null {
  return layout === 'board' ? (value ?? null) : null
}

function writeBoardAxis(
  params: URLSearchParams,
  key: string,
  layout: Layout,
  value: string | null,
  baseline: string | null,
): void {
  if (layout === 'board') writeOrClear(params, key, value, baseline)
  else params.delete(key)
}

// Applies delete-when-equal-to-baseline for one param.
function writeOrClear(params: URLSearchParams, key: string, value: string | null, baseline: string | null): void {
  if (value === baseline) params.delete(key)
  else params.set(key, value ?? '')
}

// Omitted fields leave the param as-is on the board. Written sorted so equal sets produce the same URL.
function writeVisibleFields(
  params: URLSearchParams,
  layout: Layout,
  visibleFields: readonly string[] | undefined,
  baseline: readonly string[],
): void {
  if (layout !== 'board' || (visibleFields !== undefined && sameVisibleFields(visibleFields, baseline))) {
    params.delete(VISIBLE_FIELDS_PARAM)
  } else if (visibleFields !== undefined) {
    params.set(VISIBLE_FIELDS_PARAM, [...new Set(visibleFields)].sort().join(','))
  }
}
